import { Box, Button, Checkbox, Container, FormControlLabel, TextField, Typography, IconButton, Paper } from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { Form, Formik, FieldArray } from 'formik'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import omit from 'lodash/omit'
import ImageUpload from '../../components/ImageUpload'
import api from '../../utils/api'
import { showNotification } from '../notifications'
import { showErrorNotification } from '../notifications/notificationsSlice'
import useRouteGuard from '../../hooks/useRouteGuard'
import { convertToDateTimeLocalString } from '../../utils/datetime'

const ContainerForm = ({data}: {data?: any}) => {
    useRouteGuard('canRegisterContainer', 'canViewContainers')

    const dispatch = useDispatch()
    const history = useHistory()

    return (
        <Container maxWidth="md" sx={{ p: 2 }}>
            <Formik
                initialValues={{
                    containerNumber: data?.containerNumber || '',
                    seenAt: convertToDateTimeLocalString(data?.seenAt ? new Date(data.seenAt) : new Date()),
                    location: data?.location || '',
                    trailerPlate: data?.trailerPlate || '',
                    truckPlate: data?.truckPlate || '',
                    photos: data?.photos || [],
                    intelligence: data?.intelligence || '',
                    hasVehiclePlates: (data?.vehiclePlates && data.vehiclePlates.length > 0) || false,
                    vehiclePlates: data?.vehiclePlates && data.vehiclePlates.length > 0 
                        ? data.vehiclePlates
                        : [''],
                }}
                enableReinitialize
                onSubmit={async (values) => {
                    const payload = { ...values };
                    payload.photos = values.photos.map((image: any) => image.id);
                    payload.seenAt = new Date(values.seenAt.replace(/-/g,'/').replace('T',' ')).toISOString();
                    
                    // Prepare vehicle plates for the API
                    if (values.hasVehiclePlates && values.vehiclePlates.length > 0) {
                        (payload as any).vehicle_plates = values.vehiclePlates
                            .filter((plate: string) => plate.trim() !== '')
                            .map((plate: string) => plate.trim().toUpperCase());
                    } else {
                        (payload as any).vehicle_plates = [];
                    }
                    
                    // Remove frontend-only fields
                    const cleanPayload = omit(payload, ['hasVehiclePlates', 'vehiclePlates']);
                    
                    const { error } = data 
                        ? await api.patch(`/containers/${data.id}/`, cleanPayload) 
                        : await api.post(`/containers/`, cleanPayload);
                    
                    if (error) {
                        return dispatch(showErrorNotification(error || 'Something went wrong'))
                    }
                    dispatch(
                        showNotification({
                            type: 'success',
                            body: data? 'Container updated!' : 'Container registered successfully',
                        })
                    )
                    data ? window.location.reload() : history.push('/containers')
                }}
            >
                {({ values, handleChange, handleSubmit }) => (
                    <Form>
                        <TextField
                            fullWidth
                            id="containerNumber"
                            name="containerNumber"
                            label="Container Number"
                            size="small"
                            value={values.containerNumber}
                            onChange={handleChange}
                            sx={{ mb: 2, mt: 4 }}
                            required
                        />
                        <Box mb={4}>
                            <Typography mb={0.5}>Seen at</Typography>
                            <input
                                type="datetime-local"
                                value={values.seenAt}
                                onChange={handleChange}
                                name="seenAt"
                            />
                        </Box>
                        <TextField
                            fullWidth
                            id="location"
                            name="location"
                            label="Location"
                            size="small"
                            value={values.location}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            id="trailerPlate"
                            name="trailerPlate"
                            label="Trailer Plate"
                            size="small"
                            value={values.trailerPlate}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            id="truckPlate"
                            name="truckPlate"
                            label="Truck Plate"
                            size="small"
                            value={values.truckPlate}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <ImageUpload
                            name="photos"
                            label="Images"
                            isMulti
                            placeholder="Drag and Drop here or Browser files"
                        />
                        <TextField
                            fullWidth
                            id="intelligence"
                            name="intelligence"
                            label="Intelligence"
                            size="small"
                            value={values.intelligence}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="hasVehiclePlates"
                                    checked={values.hasVehiclePlates}
                                    onChange={handleChange}
                                />
                            }
                            label="Add license plates found in this container"
                            sx={{ display: 'block', mb: 2 }}
                        />
                        
                        {values.hasVehiclePlates && (
                            <Paper sx={{ p: 3, mb: 2, backgroundColor: 'grey.50' }}>
                                <Typography variant="h6" gutterBottom>
                                    License Plates
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Enter the license plates found in this container. The system will automatically 
                                    check if vehicles exist in the SMVIU database and look up RDW data.
                                </Typography>
                                
                                <FieldArray name="vehiclePlates">
                                    {({ push, remove }) => (
                                        <Box>
                                            {values.vehiclePlates.map((plate: string, index: number) => (
                                                <Box 
                                                    key={index} 
                                                    display="flex" 
                                                    gap={2} 
                                                    mb={2} 
                                                    alignItems="center"
                                                >
                                                    <TextField
                                                        fullWidth
                                                        name={`vehiclePlates.${index}`}
                                                        label={`License Plate ${index + 1}`}
                                                        size="small"
                                                        value={plate}
                                                        onChange={handleChange}
                                                        placeholder="e.g., AB-123-CD"
                                                        inputProps={{ style: { textTransform: 'uppercase' } }}
                                                    />
                                                    
                                                    {values.vehiclePlates.length > 1 && (
                                                        <IconButton 
                                                            onClick={() => remove(index)}
                                                            color="error"
                                                            size="small"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ))}
                                            
                                            <Button
                                                startIcon={<AddIcon />}
                                                onClick={() => push('')}
                                                variant="outlined"
                                                size="small"
                                                sx={{ mt: 1 }}
                                            >
                                                Add Another License Plate
                                            </Button>
                                        </Box>
                                    )}
                                </FieldArray>
                            </Paper>
                        )}
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={() => handleSubmit()}
                            sx={{ mt: 2 }}
                        >
                            {data ? 'Update Container' : 'Register Container'}
                        </Button>
                    </Form>
                )}
            </Formik>
        </Container>
    )
}

export default ContainerForm
